import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from 'react'
import type { NotificationFilter } from './Notifications/hooks/useNotificationsReducer'

interface NotificationsPanelState {
  isOpen: boolean
  /** The tab the panel was asked to open on; null unless a page opened it. */
  filter: NotificationFilter | null
}

export interface NotificationsPanelContextValue
  extends NotificationsPanelState {
  open: (filter?: NotificationFilter) => void
  close: () => void
}

const CLOSED: NotificationsPanelState = { isOpen: false, filter: null }

/** Outside the provider nothing can open the panel. */
const UNAVAILABLE: NotificationsPanelContextValue = {
  ...CLOSED,
  open: () => undefined,
  close: () => undefined,
}

const NotificationsPanelContext =
  createContext<NotificationsPanelContextValue | null>(null)

/**
 * SN-7849 — lets a page open the header bell's notifications panel on the tab
 * it wants (Home opens it on Invitations). The panel reports back when it
 * really closes, however it was closed, so the next `open` opens it again.
 */
export const NotificationsPanelProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  'use memo'

  const [state, setState] = useState<NotificationsPanelState>(CLOSED)

  const value: NotificationsPanelContextValue = {
    ...state,
    open: (filter = 'all') => setState({ isOpen: true, filter }),
    close: () =>
      setState((current) =>
        current.isOpen || current.filter !== null ? CLOSED : current
      ),
  }

  return (
    <NotificationsPanelContext.Provider value={value}>
      {children}
    </NotificationsPanelContext.Provider>
  )
}

export const useNotificationsPanel = (): NotificationsPanelContextValue =>
  useContext(NotificationsPanelContext) ?? UNAVAILABLE
